<template>
  <v-card class="calculationsModal">
    <v-card-title> Calculate </v-card-title>
    <v-card-text>
      <div v-if="loading">
        <v-progress-circular
          :size="20"
          color="primary"
          class="mr-2"
          indeterminate
        ></v-progress-circular>
        Loading...
      </div>
      <div v-else>
        <v-form ref="form" v-model="isFormValid" lazy-validation>
          <div class="formControl" v-for="input in calculationInputs" :key="input.InputId">
            <v-select
              v-if="input.InputType === 'Lookup' || input.LookUpItems.length > 0"
              v-model="input.InputValue"
              :label="input.InputName + '*'"
              :items="input.LookUpItems"
              :placeholder="input.DefaultValue"
              item-text="Value"
              item-value="Value"
              :rules="rules"
              outlined
              dense
            />
            <v-text-field
              v-else-if="input.InputType === 'String'"
              v-model="input.InputValue"
              :label="input.InputName + '*'"
              :placeholder="input.DefaultValue"
              type="text"
              :rules="rules"
              outlined
              dense
            ></v-text-field>
            <v-text-field
              v-else-if="input.InputType === 'Number'"
              v-model="input.InputValue"
              :label="input.InputName + '*'"
              :placeholder="input.DefaultValue"
              type="number"
              :step="calcStep(input.Dp)"
              min="0"
              :rules="rules"
              outlined
              dense
            >
            </v-text-field>
            <DatePicker
              v-else-if="input.InputType === 'Date'"
              v-model="input.InputValue"
              :label="input.InputName + '*'"
              :placeholder="input.DefaultValue"
              :rules="rules"
            />
          </div>
          <div class="footerBtns">
            <v-spacer></v-spacer>
            <v-btn color="primary" outlined class="mr-4" @click="onCancel">Cancel</v-btn>
            <v-btn color="primary" depressed @click="onCalculate" :disabled="!isFormValid"
              >Calculate</v-btn
            >
          </div>
        </v-form>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import DatePicker from '@/components/formControls/DatePicker.vue';

export default {
  name: 'Calculation',
  components: { DatePicker },
  props: {
    formName: String,
    currentPlanId: Number,
    selectedMemberId: Number,
    returnInputs: Boolean,
  },

  data() {
    return {
      calculationInputs: [],
      step: 1,
      loading: false,
      rules: [(v) => !!v || 'Required'],
      isFormValid: false,
    };
  },

  mounted() {
    this.getCalculationInputs();
  },

  watch: {
    formName: function () {
      if (this.formName) {
        this.calculationInputs = [];
        this.getCalculationInputs();
      }
    },
  },

  methods: {
    onCancel() {
      this.$emit('calculateClick', 'cancel');
    },

    onCalculate() {
      this.isFormValid = this.$refs.form.validate();
      if (!this.isFormValid) return;
      const data = this.calculationInputs.map((input) => ({
        FieldName: input.InputName,
        Value: input.InputValue,
        InputType: input.InputType,
        len: input.Len,
        dp: input.Dp,
      }));
      if (this.returnInputs) {
        this.$emit('calculateClick', 'calculate', data);
        return;
      } else {
        this.calculatePlottedValues(data);
        console.log('calculated');
      }
    },

    calcStep(dp) {
      switch (dp) {
        case 0:
          return 1;
        case 1:
          return 0.5;
        case 2:
          return 0.05;
        default:
          return 1;
      }
    },

    async getCalculationInputs() {
      this.loading = true;
      try {
        const response = await this.$api(
          `/Calculation/GetCalculationInputs?formName=${this.formName}&planId=${this.currentPlanId}`
        );

        this.calculationInputs = response.data;
      } catch (error) {
        console.log(error.message);
      } finally {
        this.loading = false;
      }
    },

    async calculatePlottedValues(data) {
      this.loading = true;
      try {
        const response = await this.$api.post(
          `/Calculation/CalculatePlottedValues?memberId=${this.selectedMemberId}&formName=${this.formName}`,
          data
        );
        console.log(response.data);
        this.$emit('calculateClick', 'calculate', response.data);

        if (!response.data) {
          const msg = 'Can not calculate values';
          this.$store.commit('setNotificationMessage', { type: 'error', text: msg });
          throw new Error(msg);
        }

        if (!response.data.Values || !response.data.Values.length) {
          throw new Error('Calculation failed, try again');
        } else {
          this.$store.commit('setNotificationMessage', {
            type: 'success',
            text: 'Calculation was successful',
          });
        }
      } catch (error) {
        console.error(error.response.data);
        if (error.response?.data?.ExceptionType.includes('AbortCalculationException')) {
          const msg = `Calculation Aborted - ${error.response.data.ExceptionMessage}`;
          console.log(msg);
          this.$store.commit('setNotificationMessage', { type: 'error', text: msg });
          throw new Error(msg);
        }
        console.log(error.message);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss">
.calculationsModal {
  .footerBtns {
    display: flex;
    justify-content: flex-end;
  }
}
</style>
